import { Scrim } from "@/components/ui/Scrim";
import { SheetHeader } from "@/components/ui/Sheet";
import { Surface } from "@/components/ui/Surface";
import { useModalLayer } from "@/contexts/ModalContext";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import { cn } from "@/lib/cn";
import * as Dialog from "@radix-ui/react-dialog";
import { useRef, type MouseEvent, type ReactNode, type TouchEvent } from "react";

const SWIPE_THRESHOLD_PX = 50;

type BottomSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  contentClassName?: string;
  showHandle?: boolean;
  /** Renders a SheetHeader (Dialog.Title); without it the children must render their own title. */
  title?: ReactNode;
  description?: ReactNode;
  /** True when the children render their own `SheetHeader description`, so the dialog stays described by it. */
  describedByChildren?: boolean;
  /** Adds the header close button; usually `() => onOpenChange(false)`. */
  onClose?: () => void;
  /**
   * Radix modal mode for a sheet opened from inside another Radix modal (item screen): own interactive
   * veil, focus trap, and taps never reach the screen underneath. Leave off for top-level sheets.
   */
  modal?: boolean;
  /** Ignores focus moving outside the sheet (a page heading focused on a step change); only the scrim, X and swipe dismiss it. */
  keepOnFocusOutside?: boolean;
  /** Closes on a horizontal swipe or a downward swipe anywhere on the sheet. */
  swipeToClose?: boolean;
  /** Panel width on wide screens: `shell` (900px), `detail` (48rem, an option photo sheet) or `form` (40rem, a note). */
  width?: "shell" | "detail" | "form";
  /** `narrow` centres header and children together in a `max-w-md` column (a decision sheet on desktop). */
  contentWidth?: "full" | "narrow";
  /** Hairline under the `title` header. */
  headerBordered?: boolean;
  /** Alignment of the `title` header: `center` for a detail sheet with a centred photo. */
  headerAlign?: "start" | "center";
};

const WIDTH = { shell: "max-w-shell", detail: "max-w-detail", form: "max-w-form" } as const;

/**
 * Shared bottom sheet: scrim plus a panel sliding from the bottom, layered by `useModalLayer`.
 * Default `modal={false}` is deliberate: Radix modal mode sets `pointer-events: none` on `<body>` and kills the
 * app's non-Radix overlays (Pix copy mid-payment); scroll lock comes from `useLockBodyScroll` (docs/Z-INDEX-LAYERS.md).
 */
export function BottomSheet({
  open,
  onOpenChange,
  children,
  contentClassName,
  showHandle = true,
  title,
  description,
  describedByChildren = false,
  onClose,
  modal = false,
  keepOnFocusOutside = false,
  swipeToClose = false,
  width = "shell",
  contentWidth = "full",
  headerBordered = false,
  headerAlign = "start",
}: BottomSheetProps) {
  const { zIndex, backdropZIndex } = useModalLayer(open);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  useLockBodyScroll(open);

  function handleScrimClick(event: MouseEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    onOpenChange(false);
  }

  function handleTouchStart(event: TouchEvent<HTMLDivElement>) {
    const touch = event.touches[0];
    touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  }

  function handleTouchEnd(event: TouchEvent<HTMLDivElement>) {
    const start = touchStart.current;
    const touch = event.changedTouches[0];
    touchStart.current = null;
    if (!start || !touch) return;
    const diffX = start.x - touch.clientX;
    const diffY = start.y - touch.clientY;
    const horizontal = Math.abs(diffX) > Math.abs(diffY);
    if ((horizontal && Math.abs(diffX) > SWIPE_THRESHOLD_PX) || (!horizontal && diffY < -SWIPE_THRESHOLD_PX)) {
      onOpenChange(false);
    }
  }

  const scrim = (
    <Scrim
      zIndex={backdropZIndex}
      data-bottom-sheet-scrim=""
      className="pointer-events-auto animate-fade-in motion-reduce:animate-none"
      onClick={handleScrimClick}
    />
  );

  const header =
    title !== undefined ? (
      <SheetHeader title={title} description={description} onClose={onClose} bordered={headerBordered} align={headerAlign} />
    ) : null;

  return (
    <Dialog.Root modal={modal} open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {modal ? <Dialog.Overlay asChild>{scrim}</Dialog.Overlay> : scrim}
        <Dialog.Content
          asChild
          {...(description || describedByChildren ? {} : { "aria-describedby": undefined })}
          onFocusOutside={keepOnFocusOutside ? (event) => event.preventDefault() : undefined}
          onClick={modal ? (event) => event.stopPropagation() : undefined}
          onTouchStart={swipeToClose ? handleTouchStart : undefined}
          onTouchEnd={swipeToClose ? handleTouchEnd : undefined}
        >
          <Surface
            surface="card"
            style={{ zIndex }}
            className={cn(
              "pointer-events-auto fixed bottom-0 left-0 right-0 mx-auto overflow-hidden",
              WIDTH[width],
              "rounded-t-sheet shadow-overlay",
              "data-[state=open]:animate-slide-in-from-bottom data-[state=closed]:animate-slide-out-to-bottom",
              contentClassName,
            )}
          >
            {showHandle && (
              <div className="flex justify-center pb-1 pt-3">
                <div className="h-1.5 w-12 rounded-full bg-card-text/20" />
              </div>
            )}
            {contentWidth === "narrow" ? (
              <div className="mx-auto flex w-full max-w-md flex-col">
                {header}
                {children}
              </div>
            ) : (
              <>
                {header}
                {children}
              </>
            )}
          </Surface>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
